import * as React from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "../lib/cn";

export interface DetailHeaderProps {
  /** Called when the back control is activated. Router-agnostic on purpose — the caller wires this to its own navigation. */
  onBack: () => void;
  backLabel?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Trailing controls (e.g. an Archive button). */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * A "single main pane navigated into detail" header: a big, obvious way
 * back to the list (not just browser back), a title/subtitle block, and an
 * optional trailing actions slot. Shared by any detail view that replaces
 * a list in place (see mail-web's ThreadDetail) — visible at every
 * viewport size, since in a single-pane layout there's no persistent list
 * alongside it to fall back on.
 */
export function DetailHeader({ onBack, backLabel = "Back", title, subtitle, actions, className }: DetailHeaderProps) {
  return (
    <div className={cn("flex items-center gap-2 border-b border-border px-3 py-2", className)}>
      <button
        type="button"
        onClick={onBack}
        aria-label={backLabel}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-charcoal hover:bg-parchment-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
      >
        <ArrowLeft size={22} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-lg text-charcoal">{title}</p>
        {subtitle && <p className="truncate text-xs text-muted">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}
